import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Lock,
  MessageCircle,
  QrCode,
  ShieldCheck,
  Trash2,
  UserRoundCheck,
} from 'lucide-react';
import { CNPJ, NOME_FANTASIA } from './constantes';
import base from './publico.module.css';
import css from './Secoes.module.css';

/**
 * Segurança e confiança.
 *
 * Só sinais verificáveis: o que o sistema faz (ver a Política de Privacidade,
 * que descreve o mesmo) e quem é a empresa. Nada de selo, depoimento ou número
 * de clientes que não dê para provar.
 */
export function Seguranca() {
  return (
    <section id="seguranca" className={`${base.secao} ${css.seguranca}`}>
      <div className={`${base.conteudo} ${css.segurancaGrade}`}>
        <div className={css.segurancaAbertura}>
          <span className={css.sobretituloEscuro}>Segurança e confiança</span>
          <h2 className={css.tituloEscuro}>
            Feito para quem responde pelo dado do cliente
          </h2>
          <p className={css.introEscura}>
            Dado fiscal é dado sensível. Por isso o que fazemos com ele está
            escrito, em linguagem direta, na Política de Privacidade — e o
            sistema faz exatamente o que ela diz.
          </p>

          <div className={css.ficha}>
            <span className={css.fichaRotulo}>Quem está por trás</span>
            <dl className={css.fichaDados}>
              <div>
                <dt>Empresa</dt>
                <dd>{NOME_FANTASIA}</dd>
              </div>
              <div>
                <dt>CNPJ</dt>
                <dd>{CNPJ}</dd>
              </div>
              <div>
                <dt>Atendimento</dt>
                <dd>
                  <MessageCircle aria-hidden="true" /> Chat de suporte no
                  painel, em dias úteis
                </dd>
              </div>
            </dl>
            <Link to="/privacidade" className={css.fichaLink} data-clique="privacidade-seguranca">
              Ler a Política de Privacidade <ArrowRight aria-hidden="true" />
            </Link>
          </div>
        </div>

        <div className={css.segurancaItens}>
          <ItemSeguranca icone={<BadgeCheck />} titulo="O XML original">
            Entregamos o documento fiscal de verdade, com a assinatura digital
            do emitente — não uma reconstrução a partir do PDF.
          </ItemSeguranca>
          <ItemSeguranca icone={<ShieldCheck />} titulo="LGPD na prática">
            Sem cookie de publicidade nem rastreador de terceiros. A medição de
            audiência é anônima e só roda se você permitir.
          </ItemSeguranca>
          <ItemSeguranca icone={<Lock />} titulo="Sessão protegida">
            O token de acesso fica em cookie que o JavaScript da página não
            consegue ler, com proteção contra CSRF em toda escrita. Tráfego
            cifrado por HTTPS e senha guardada só como hash.
          </ItemSeguranca>
          <ItemSeguranca icone={<UserRoundCheck />} titulo="Os dados são do cliente">
            Cada busca e cada análise pertencem a quem as criou. Nenhuma conta
            enxerga o que é de outra.
          </ItemSeguranca>
          <ItemSeguranca icone={<QrCode />} titulo="Pix pelo Asaas">
            A cobrança é emitida pelo Asaas, instituição de pagamento. Não
            recebemos nem armazenamos dados de cartão.
          </ItemSeguranca>
          <ItemSeguranca icone={<Trash2 />} titulo="Nada fica esquecido">
            As buscas e os arquivos são removidos automaticamente 48 horas após
            a conclusão. O ZIP é seu; a cópia no servidor, não.
          </ItemSeguranca>
        </div>
      </div>
    </section>
  );
}

function ItemSeguranca({
  icone,
  titulo,
  children,
}: {
  icone: ReactNode;
  titulo: string;
  children: ReactNode;
}) {
  return (
    <div className={css.itemSeguranca}>
      <span className={css.iconeEscuro} aria-hidden="true">
        {icone}
      </span>
      <h3>{titulo}</h3>
      <p>{children}</p>
    </div>
  );
}
